import { ChevronDown } from 'lucide-react'
import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { KeyboardEvent, ReactNode, SelectHTMLAttributes } from 'react'

import { cn } from '@/shared/lib/cn'

import { OptionList } from './listbox'
import { UMBRAL_DE_BUSQUEDA, optionId, useListNav, type ListOption } from './listbox-nav'
import { Popover } from './Popover'

export const CONTROL =
  'hairline w-full rounded-[var(--radius-control)] bg-surface-1 px-2 text-xs text-text-1 ' +
  'placeholder:text-text-3 disabled:opacity-50'

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'multiple' | 'size'>

/**
 * El desplegable de la app. **Misma API que un `<select>`**: `<option>` como
 * hijos, `value`, y un `onChange` que recibe un evento real con
 * `event.target.value`.
 *
 * Por qué no un `<select>` nativo: su menú lo dibuja el sistema operativo, no
 * respeta el tema, no se puede buscar en él y en cada navegador se ve distinto.
 * En una herramienta que se mira ocho horas por día, el control más usado no
 * puede ser el único que no sigue el diseño.
 *
 * Por qué la misma API: hay una veintena de usos, y cambiar la forma de todos
 * para cambiar cómo se dibuja uno es la clase de refactor que deja bugs. Por
 * debajo vive un `<select>` oculto con las mismas opciones: elegir en el panel
 * le setea el valor y dispara `change`, y React se lo entrega al `onChange` de
 * siempre. El `ref` apunta a ese `<select>`.
 *
 * Accesibilidad: el disparador es un `combobox` con su `listbox`; el foco no
 * sale del disparador (o del buscador) y la opción activa se anuncia con
 * `aria-activedescendant`. Flechas, Home/End, Enter, Escape y escritura.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    className,
    children,
    id,
    value,
    defaultValue,
    onChange,
    disabled,
    title,
    name,
    required,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  },
  ref,
) {
  const nativo = useRef<HTMLSelectElement>(null)
  useImperativeHandle(ref, () => nativo.current as HTMLSelectElement)

  const boton = useRef<HTMLButtonElement>(null)
  const generado = useId()
  const listId = `${id ?? generado}-lista`

  const opciones = useMemo(() => leerOpciones(children), [children])
  const [interno, setInterno] = useState(() => String(defaultValue ?? opciones[0]?.value ?? ''))
  const actual = value !== undefined && value !== null ? String(value) : interno
  // Igual que el nativo: un valor que no está entre las opciones muestra la primera.
  const elegida = opciones.find((o) => o.value === actual) ?? opciones[0]

  const [open, setOpen] = useState(false)
  const nav = useListNav(
    opciones,
    open,
    opciones.findIndex((o) => o.value === elegida?.value),
  )
  const searchable = opciones.length > UMBRAL_DE_BUSQUEDA

  const cerrar = useCallback(() => setOpen(false), [])

  const elegir = (opcion: ListOption) => {
    setOpen(false)
    boton.current?.focus()
    if (opcion.value === actual) return
    setInterno(opcion.value)
    const select = nativo.current
    if (!select) return
    // El setter nativo y no `select.value = ...`: React vigila esa propiedad, y
    // asignarla por el camino normal le haría creer que no cambió nada.
    Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set?.call(
      select,
      opcion.value,
    )
    select.dispatchEvent(new Event('change', { bubbles: true }))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        setOpen(true)
        return
      }
      // Escribir sobre el disparador cerrado: con buscador, abre buscando; sin
      // él, salta a la próxima opción que empieza con esa letra, como el nativo.
      if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        if (searchable) {
          event.preventDefault()
          setOpen(true)
          nav.setQuery(event.key)
        } else {
          const proxima = siguientePorLetra(opciones, actual, event.key)
          if (proxima) elegir(proxima)
        }
      }
      return
    }

    if (event.key === 'Escape') {
      // Se corta acá: si el desplegable vive en un panel, el Escape cierra el
      // desplegable, no el panel entero.
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      boton.current?.focus()
      return
    }
    if (event.key === 'Tab') {
      setOpen(false)
      return
    }
    if (event.key === ' ' && !searchable) {
      event.preventDefault()
      const opcion = nav.visibles[nav.active]
      if (opcion && !opcion.disabled) elegir(opcion)
      return
    }
    if (nav.onKey(event, elegir)) event.preventDefault()
  }

  return (
    <>
      <button
        ref={boton}
        type="button"
        id={id}
        role="combobox"
        // El valor elegido, legible desde afuera sin abrir el panel: lo usan los
        // e2e para afirmar qué quedó elegido sin depender del texto traducido.
        data-value={elegida?.value ?? ''}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && !searchable ? optionId(listId, nav.active) : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-required={required || undefined}
        title={title}
        disabled={disabled}
        onClick={() => setOpen((abierto) => !abierto)}
        onKeyDown={onKeyDown}
        className={cn(
          CONTROL,
          'flex h-7 items-center gap-1.5 text-left',
          'focus-visible:outline-2 focus-visible:outline-signal',
          open && 'outline-2 outline-signal',
          className,
        )}
      >
        {elegida?.leading}
        <span className="min-w-0 flex-1 truncate">{elegida?.label}</span>
        <ChevronDown size={12} aria-hidden="true" className="shrink-0 text-text-3" />
      </button>

      {/* El `<select>` de verdad: guarda el valor y emite el `change`. */}
      <select
        ref={nativo}
        name={name}
        required={required}
        value={actual}
        onChange={onChange ?? (() => undefined)}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
      >
        {/* Sólo los valores: con el texto, cada opción existiría dos veces en la
            página y un "buscá el texto Pausada" encontraría la oculta también. */}
        {opciones.map((opcion) => (
          <option key={opcion.value} value={opcion.value} disabled={opcion.disabled} />
        ))}
      </select>

      <Popover anchor={boton} open={open} onClose={cerrar} matchWidth className="max-w-[360px]">
        <OptionList
          id={listId}
          label={ariaLabel ?? elegida?.text ?? ''}
          nav={nav}
          isSelected={(v) => v === elegida?.value}
          onPick={elegir}
          searchable={searchable}
          onSearchKeyDown={onKeyDown}
        />
      </Popover>
    </>
  )
})

/** Las `<option>` de los hijos, aplanando listas y descartando `null`. */
function leerOpciones(children: ReactNode): ListOption[] {
  const opciones: ListOption[] = []
  Children.forEach(children, (hijo) => {
    if (!isValidElement<{ value?: unknown; disabled?: boolean; children?: ReactNode }>(hijo)) {
      return
    }
    if (hijo.type !== 'option') return
    const texto = textoDe(hijo.props.children)
    opciones.push({
      value: hijo.props.value === undefined ? texto : String(hijo.props.value),
      label: hijo.props.children,
      text: texto,
      disabled: Boolean(hijo.props.disabled),
    })
  })
  return opciones
}

function textoDe(nodo: ReactNode): string {
  if (nodo === null || nodo === undefined || typeof nodo === 'boolean') return ''
  if (typeof nodo === 'string' || typeof nodo === 'number') return String(nodo)
  if (Array.isArray(nodo)) return nodo.map(textoDe).join('')
  if (isValidElement<{ children?: ReactNode }>(nodo)) return textoDe(nodo.props.children)
  return ''
}

function siguientePorLetra(
  opciones: readonly ListOption[],
  actual: string,
  letra: string,
): ListOption | undefined {
  const l = letra.toLowerCase()
  const desde = opciones.findIndex((o) => o.value === actual)
  for (let n = 1; n <= opciones.length; n++) {
    const opcion = opciones[(desde + n) % opciones.length]
    if (opcion && !opcion.disabled && opcion.text.toLowerCase().startsWith(l)) return opcion
  }
  return undefined
}
